// Hatha Yoga Bath - Service + Location Page (SEO Enhanced)
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, MapPin, Car, Check, Users, Clock, Heart } from "lucide-react";
import Navigation from "../../../components/shared/Navigation";
import Footer from "../../../components/shared/Footer";
import PageBreadcrumb from "../../../components/shared/PageBreadcrumb";
import { usePageMeta, generateServiceSchema, generateFAQSchema, SchemaScript } from "../../../components/shared/SEOHelper";

const HathaYogaBath = () => {
  usePageMeta({
    title: "Hatha Yoga Bath | Yoga Classes | Annelie Lily",
    description: "Hatha yoga classes for Bath residents. Traditional yoga building strength and flexibility, based in Frome, 25 minutes from Bath.",
    canonical: "https://annelielily.com/areas/yoga-bath/hatha-yoga"
  });

  const serviceSchema = generateServiceSchema({
    name: "Hatha Yoga Classes for Bath",
    description: "Hatha yoga classes for Bath residents at Annelie Lily Yoga in Frome. Traditional practice building strength, flexibility, and calm.",
    serviceType: "Yoga Classes",
    url: "https://annelielily.com/areas/yoga-bath/hatha-yoga"
  });

  const faqs = [
    { question: "Where are Hatha yoga classes held?", answer: "All classes are at my studio in Frome, about 25 minutes from Bath via the A36. Many Bath residents make this journey weekly." },
    { question: "What is Hatha yoga?", answer: "Hatha is traditional yoga combining postures (asanas), breathing (pranayama), and relaxation. It builds strength and flexibility while calming the mind." },
    { question: "Is Hatha yoga suitable for beginners?", answer: "Yes. I offer modifications for every pose, so you can work at your own level. Many of my students started as complete beginners." },
    { question: "What should I bring to class?", answer: "Just comfortable clothing you can move in. Mats and props are provided, though you're welcome to bring your own mat if you prefer." },
    { question: "How often should I practice Hatha yoga?", answer: "Once a week is a good starting point. Many students find twice weekly helps them progress faster and maintain the benefits between sessions." },
  ];

  const faqSchema = generateFAQSchema(faqs);

  return (
    <div className="min-h-screen bg-cream">
      <SchemaScript schema={serviceSchema} />
      <SchemaScript schema={faqSchema} />
      <Navigation />
      <PageBreadcrumb currentPage="Hatha Yoga" currentSlug="areas/yoga-bath/hatha-yoga" />
      
      {/* Hero */}
      <section className="pt-8 pb-16 px-6 md:px-12 bg-muted">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <span className="text-xs uppercase tracking-[0.2em] text-gold font-semibold mb-3 block">Hatha Yoga for Bath</span>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-light text-neutral-800 mb-5">Hatha Yoga Bath</h1>
            <p className="text-neutral-700 leading-relaxed max-w-2xl mx-auto mb-6">
              Traditional Hatha yoga classes for Bath residents. Build strength, improve flexibility, 
              and find calm — all in small, welcoming classes just 25 minutes from Bath city centre.
            </p>
            <div className="flex flex-wrap justify-center gap-4 mb-8">
              <span className="flex items-center gap-2 bg-white/70 px-4 py-2 rounded-full text-sm text-neutral-700"><MapPin className="w-4 h-4 text-gold" />Based in Frome</span>
              <span className="flex items-center gap-2 bg-white/70 px-4 py-2 rounded-full text-sm text-neutral-700"><Car className="w-4 h-4 text-gold" />25 min from Bath</span>
            </div>
            <Link to="/contact" className="btn-primary" data-testid="hero-cta">Book a Hatha Class <ArrowRight className="w-4 h-4 inline ml-2" /></Link>
          </motion.div>
        </div>
      </section>

      {/* Featured Snippet / Direct Answer */}
      <section className="py-10 px-6 md:px-12 bg-cream border-b border-gold/10">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-heading text-xl md:text-2xl text-neutral-800 mb-3">What is Hatha yoga?</h2>
          <p className="text-neutral-700">
            Hatha yoga is traditional yoga combining physical postures (asanas), breathing techniques (pranayama), and relaxation. It's the foundation of most modern yoga styles, building strength and flexibility while calming the mind. Suitable for all levels.
          </p>
        </div>
      </section>

      {/* Who Is This For */}
      <section className="py-16 px-6 md:px-12 bg-cream">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-8">Who is Hatha yoga for?</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {[
              { title: "Complete beginners", desc: "Learn the foundations properly with modifications for every pose" },
              { title: "Those seeking balance", desc: "Equal focus on strength, flexibility, and relaxation" },
              { title: "Desk workers", desc: "Counter the effects of sitting with stretches and strengthening" },
              { title: "Anyone wanting calm", desc: "Each class ends with guided relaxation to quiet the mind" },
            ].map((item, i) => (
              <div key={i} className="bg-white/70 p-5 rounded-xl border border-gold/10">
                <h3 className="font-heading text-lg text-neutral-800 mb-2">{item.title}</h3>
                <p className="text-neutral-600 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What to Expect */}
      <section className="py-16 px-6 md:px-12 bg-muted">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-8">What to expect in a Hatha class</h2>
          <div className="space-y-6 text-neutral-700">
            <div className="flex gap-4">
              <div className="w-8 h-8 bg-gold/20 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                <span className="text-gold font-semibold text-sm">1</span>
              </div>
              <div>
                <h3 className="font-heading text-lg text-neutral-800 mb-1">Centering & breath awareness</h3>
                <p className="text-sm">We begin by settling in, leaving the day behind, and connecting with the breath.</p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="w-8 h-8 bg-gold/20 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                <span className="text-gold font-semibold text-sm">2</span>
              </div>
              <div>
                <h3 className="font-heading text-lg text-neutral-800 mb-1">Standing poses</h3>
                <p className="text-sm">Building strength, balance, and grounding through poses like Warrior, Triangle, and Tree.</p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="w-8 h-8 bg-gold/20 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                <span className="text-gold font-semibold text-sm">3</span>
              </div>
              <div>
                <h3 className="font-heading text-lg text-neutral-800 mb-1">Floor work</h3>
                <p className="text-sm">Seated stretches, twists for the spine, and gentle inversions to release tension.</p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="w-8 h-8 bg-gold/20 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                <span className="text-gold font-semibold text-sm">4</span>
              </div>
              <div>
                <h3 className="font-heading text-lg text-neutral-800 mb-1">Deep relaxation</h3>
                <p className="text-sm">Every class ends with guided relaxation — time for body and mind to integrate the practice.</p>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Why Bath Residents Choose Us */}
      <section className="py-16 px-6 md:px-12 bg-cream">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-6">Why Bath residents travel to Frome</h2>
          <div className="prose-content space-y-4 text-neutral-700">
            <p>
              If you live in <Link to="/areas/yoga-bath" className="text-gold hover:underline">Bath</Link> and you're looking 
              for Hatha yoga, Frome is just 25 minutes away. The A36 makes it an easy drive, and many of 
              my students say the journey becomes part of the wind-down.
            </p>
            <p>
              My classes are kept small (6-8 people) so there's space for individual guidance. Whether you're 
              <Link to="/yoga-classes/beginner-yoga" className="text-gold hover:underline"> new to yoga</Link> or have been practising 
              for years, you'll find a supportive environment quite different from larger Bath studios.
            </p>
            <p>
              If you prefer something slower, I also offer <Link to="/areas/yoga-bath/yin-yoga" className="text-gold hover:underline">Yin yoga for Bath</Link> residents, 
              or <Link to="/areas/yoga-bath/private-yoga" className="text-gold hover:underline">private sessions</Link> for one-to-one attention.
            </p>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 px-6 md:px-12 bg-muted">
        <div className="max-w-6xl mx-auto">
          <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-10 text-center">Benefits of Hatha Yoga</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { icon: Users, text: "Small group attention" },
              { icon: Clock, text: "Work at your pace" },
              { icon: Heart, text: "Reduces stress" },
              { icon: Check, text: "All levels welcome" },
            ].map((item, i) => (
              <div key={i} className="bg-white/70 p-5 rounded-xl border border-gold/10 flex items-center gap-3">
                <item.icon className="w-5 h-5 text-gold flex-shrink-0" />
                <span className="text-neutral-700">{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 px-6 md:px-12 bg-cream">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-8 text-center">Questions About Hatha Yoga in Bath</h2>
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-white/70 p-5 rounded-xl border border-gold/10">
                <h3 className="font-heading text-lg text-neutral-800 mb-2">{faq.question}</h3>
                <p className="text-neutral-600 text-sm">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section className="py-12 px-6 md:px-12 bg-muted">
        <div className="max-w-3xl mx-auto text-center">
          <p className="text-neutral-700 mb-4">
            I'm Annelie, a qualified yoga teacher offering small, welcoming classes where everyone gets individual attention. 
            <Link to="/about" className="text-gold hover:underline ml-1">Learn more about my approach</Link>, or 
            <Link to="/about" className="text-gold hover:underline ml-1">read what students say</Link>.
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-6 md:px-12 bg-cream">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-4">Ready to Try Hatha Yoga?</h2>
          <p className="text-neutral-600 mb-6">Get in touch and I'll help you find the right class.</p>
          <Link to="/contact" className="btn-primary" data-testid="bottom-cta">Book a Class <ArrowRight className="w-4 h-4 inline ml-2" /></Link>
        </div>
      </section>

      {/* Related Services */}
      <section className="py-12 px-6 md:px-12 bg-muted">
        <div className="max-w-6xl mx-auto text-center">
          <p className="text-neutral-600 text-sm mb-4">Also available for Bath residents:</p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link to="/areas/yoga-bath/yin-yoga" className="text-sm text-gold hover:underline">Yin Yoga Bath</Link>
            <span className="text-neutral-300">|</span>
            <Link to="/areas/yoga-bath/private-yoga" className="text-sm text-gold hover:underline">Private Sessions Bath</Link>
            <span className="text-neutral-300">|</span>
            <Link to="/areas/yoga-bath" className="text-sm text-gold hover:underline">All Yoga Classes Bath</Link>
            <span className="text-neutral-300">|</span>
            <Link to="/hatha-yoga" className="text-sm text-gold hover:underline">Hatha Yoga (Main Page)</Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HathaYogaBath;
